import {useContext, useEffect, useMemo, useState} from 'react';
import useExperiment from '../../../../hooks/useExperiment';
import useGiftAdvisorV2Flag from '../../../../hooks/useGiftAdvisorV2Flag';
import getOverrideParams from '../../../../utils/parseUrlParams';
import {
  EXPERIMENT_CONFIGS,
  EXPERIMENT_GIFT_ADVISOR_V2,
} from '../../../../constants/experiments';
import AppContext from '../../../AppContext';
import UserStateContext from '../../../UserStateContext';

const useGiftAdvisorV2Experiment = () => {
  const {hasVisitedManage, hasVisitedOverview} = useContext(AppContext);
  const {userStateData, isFetching: isFetchingUserData = true} = useContext(
    UserStateContext,
  );
  const [hasQualified, setHasQualified] = useState(false);

  const experimentName = EXPERIMENT_GIFT_ADVISOR_V2;

  const config = EXPERIMENT_CONFIGS[experimentName]?.[process.env.ENV];
  const {experimentId, variants} = config ?? {};

  const controlVariantId = variants?.control?.id;
  const variantId = variants?.variant?.id;

  const hasLandedOnManage = Boolean(hasVisitedManage);
  const hasLandedOnOverview = Boolean(hasVisitedOverview);

  /**
   * Overview (and `/`) redirect to onboarding when the user has not visited
   * the registry. Manage does not. Require visited-registry only for the
   * Overview eligibility path so we do not bucket users who are about to be
   * redirected.
   */
  const hasUserVisitedRegistry =
    !isFetchingUserData &&
    (userStateData?.hasVisitedRegistry ||
      typeof userStateData?.registryFirstVisitDate === 'string');

  /**
   * Users are eligible once they land on Manage or Overview (and, for Overview,
   * will not be redirected to onboarding). Item count is not required — users
   * with 0 items are still bucketed. The Unleash flag gates Flipper assignment.
   */
  const meetsBucketingCriteria =
    hasLandedOnManage || (hasLandedOnOverview && hasUserVisitedRegistry);

  /**
   * Qualification is sticky for the session so navigation or user-state
   * refetch cannot disable the experiment and drop an existing assignment.
   */
  useEffect(() => {
    if (meetsBucketingCriteria && !hasQualified) setHasQualified(true);
  }, [meetsBucketingCriteria, hasQualified]);

  const isEligible = hasQualified || meetsBucketingCriteria;

  const isFeatureFlagEnabled = useGiftAdvisorV2Flag();

  const override = getOverrideParams(experimentId);
  const hasOverride = typeof override === 'string';

  // !!experimentId keeps flipper inert in environments without a configured id
  const shouldUseExperiment =
    !hasOverride && isFeatureFlagEnabled && isEligible && !!experimentId;

  const {
    isLoading,
    isActive,
    variant: hookVariant,
    isInControl: hookIsInControl,
  } = useExperiment({
    name: experimentName,
    isEnabled: shouldUseExperiment,
  });

  const resolvedVariant = hasOverride ? override : hookVariant;

  const isExperimentLoading = useMemo(() => {
    if (hasOverride) return false;
    if (!experimentId) return false;
    // The flag being off resolves the experiment to control — nothing to wait for.
    if (!isFeatureFlagEnabled) return false;

    if (!isEligible) {
      // Manage visit alone is enough to resolve eligibility (positive).
      if (hasLandedOnManage) return false;

      // Overview: wait for user state; once known, ineligible users (about to
      // redirect to onboarding) are resolved.
      if (hasLandedOnOverview) {
        return isFetchingUserData;
      }

      // Have not landed on Manage or Overview yet — keep waiting.
      return true;
    }

    return isLoading;
  }, [
    hasOverride,
    experimentId,
    isFeatureFlagEnabled,
    isEligible,
    hasLandedOnManage,
    hasLandedOnOverview,
    isFetchingUserData,
    isLoading,
  ]);

  const isInVariant = useMemo(() => {
    if (!variantId) return false;

    if (hasOverride) {
      return override?.toString().trim() === variantId.toString().trim();
    }

    if (!isActive) return false;

    return resolvedVariant === variantId;
  }, [variantId, hasOverride, override, isActive, resolvedVariant]);

  const isInControl = useMemo(() => {
    if (!controlVariantId) return false;

    if (hasOverride) {
      return override?.toString().trim() === controlVariantId.toString().trim();
    }

    if (!isActive) return true;

    return !!hookIsInControl;
  }, [controlVariantId, hasOverride, override, isActive, hookIsInControl]);

  return useMemo(() => {
    return {
      experimentId,
      variant: resolvedVariant,
      isInVariant,
      isInControl,
      isExperimentLoading,
      isFeatureFlagEnabled,
      // True once Flipper has assigned (or a URL override is present). Used by
      // Overview so control users still see classic GA when V2 is live, without
      // treating "flag on but not yet eligible" as control.
      isAssigned: hasOverride || isActive,
    };
  }, [
    experimentId,
    resolvedVariant,
    isInVariant,
    isInControl,
    isExperimentLoading,
    isFeatureFlagEnabled,
    hasOverride,
    isActive,
  ]);
};

export default useGiftAdvisorV2Experiment;
